import { authService } from '@/services/auth';
import type { Organization } from '@/types';

const API_BASE = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8081';
const ORG_STORAGE_KEY = 'statsmatic_currentOrganizationId';

class ApiService {
  private token: string | null = null;  organizationId: string | null = null;

  constructor() {
    this.organizationId = localStorage.getItem(ORG_STORAGE_KEY);
  }

  setToken(token: string | null) {
    this.token = token;
  }

  setOrganizationId(orgId: string | null) {
    this.organizationId = orgId;
    if (orgId) {
      localStorage.setItem(ORG_STORAGE_KEY, orgId);
    }
  }

  private async refreshToken(): Promise<string | null> {
    // Clerk mints fresh 60s tokens on demand — no refresh bookkeeping needed
    try {
      this.token = await authService.getToken();
      return this.token;
    } catch {
      return null;
    }
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    skipOrgHeader = false,
    isRetry = false
  ): Promise<T> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    // Fetch a fresh token per request — Clerk tokens expire after 60s
    const token = await authService.getToken().catch(() => this.token);
    if (token) {
      this.token = token;
      headers['Authorization'] = `Bearer ${token}`;
    }

    if (this.organizationId && !skipOrgHeader) {
      headers['X-Organization-Id'] = this.organizationId;
    }

    const response = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      headers,
    });

    // Handle 401 by attempting token refresh (only once)
    if (response.status === 401 && !isRetry) {
      const newToken = await this.refreshToken();
      if (newToken) {
        // Retry the request with the new token
        return this.request<T>(endpoint, options, skipOrgHeader, true);
      }
      // Refresh failed - will throw below
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Request failed' }));
      throw new Error(error.error || `HTTP ${response.status}`);
    }

    return response.json();
  }

  // HTTP method helpers
  async get<T>(endpoint: string, options?: { params?: Record<string, string | number> }): Promise<{ data: T }> {
    let url = endpoint;
    if (options?.params) {
      const searchParams = new URLSearchParams();
      Object.entries(options.params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, String(value));
        }
      });
      const query = searchParams.toString();
      if (query) {
        url += `?${query}`;
      }
    }
    const data = await this.request<T>(url);
    return { data };
  }

  async post<T>(endpoint: string, body?: unknown): Promise<{ data: T }> {
    const data = await this.request<T>(endpoint, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    });
    return { data };
  }

  async put<T>(endpoint: string, body?: unknown): Promise<{ data: T }> {
    const data = await this.request<T>(endpoint, {
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    });
    return { data };
  }

  async delete<T>(endpoint: string): Promise<{ data: T }> {
    const data = await this.request<T>(endpoint, { method: 'DELETE' });
    return { data };
  }

  // Auth
  async verifyAuth(): Promise<{ user: { id: string; email: string }; organizations: Organization[] }> {
    return this.request('/api/auth/verify', { method: 'POST' });
  }

  async getMe(): Promise<{ user: { id: string; email: string }; organizations: Organization[] }> {
    return this.request('/api/auth/me');
  }

  async deleteAccount(): Promise<void> {
    return this.request('/api/auth/delete-account', { method: 'DELETE' });
  }

  // Organizations
  async listOrganizations(): Promise<Organization[]> {
    const response = await this.request<{ organizations: Organization[] }>('/api/organizations', {}, true);
    return response.organizations;
  }

  async getOrganization(id: string): Promise<{ organization: Organization; role: string }> {
    return this.request(`/api/organizations/${id}`);
  }

  async createOrganization(data: { name: string }): Promise<Organization> {
    return this.request('/api/organizations', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateOrganization(id: string, data: Partial<Organization>): Promise<Organization> {
    return this.request(`/api/organizations/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteOrganization(id: string): Promise<void> {
    return this.request(`/api/organizations/${id}`, { method: 'DELETE' });
  }

  // Sites
  async listSites(): Promise<{ sites: Array<{ id: string; name: string; domain: string; tracking_code: string }> }> {
    return this.request('/api/sites');
  }

  async getSite(id: string): Promise<{ site: { id: string; name: string; domain: string; tracking_code: string } }> {
    return this.request(`/api/sites/${id}`);
  }

  async createSite(data: { name: string; domain: string }): Promise<{ site: { id: string; name: string; domain: string; tracking_code: string } }> {
    return this.request('/api/sites', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateSite(id: string, data: { name?: string; domain?: string }): Promise<{ site: { id: string; name: string; domain: string; tracking_code: string } }> {
    return this.request(`/api/sites/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteSite(id: string): Promise<void> {
    return this.request(`/api/sites/${id}`, { method: 'DELETE' });
  }

  // Analytics
  async getAnalytics(siteId: string, params?: { start_date?: string; end_date?: string }): Promise<unknown> {
    const searchParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value) searchParams.append(key, value);
      });
    }
    const query = searchParams.toString();
    return this.request(`/api/sites/${siteId}/analytics${query ? `?${query}` : ''}`);
  }

  // User Preferences
  async getUserPreferences(): Promise<{ user_id: string; timezone: string }> {
    return this.request('/api/user/preferences');
  }

  async updateUserPreferences(data: { timezone?: string }): Promise<{ user_id: string; timezone: string }> {
    return this.request('/api/user/preferences', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }
}

export const api = new ApiService();
export default api;
